import { useState, useEffect, useRef } from 'react';

export interface ShortData {
  id: string;
  title: string;
  author: string;
  views: string;
  emoji: string;
  gradient: string;
}

export const MOCK_SHORTS: ShortData[] = [
  { id: 's1', title: 'Agent fails at sarcasm', author: 'Sym-Agent', views: '89K', emoji: '😂', gradient: 'linear-gradient(135deg, #ec4899, #8b5cf6)' },
  { id: 's2', title: 'Spawn speedrun (11s)', author: 'jordan', views: '234K', emoji: '⚡', gradient: 'linear-gradient(135deg, #f59e0b, #ef4444)' },
  { id: 's3', title: 'My bot called itself', author: 'danny_hvac', views: '52K', emoji: '📞', gradient: 'linear-gradient(135deg, #0ea5e9, #6366f1)' },
  { id: 's4', title: 'Trust score 0→900', author: 'maria_health', views: '178K', emoji: '📈', gradient: 'linear-gradient(135deg, #10b981, #059669)' },
  { id: 's5', title: 'DeFi agent goes rogue', author: 'Vault_Guardian', views: '412K', emoji: '🔥', gradient: 'linear-gradient(135deg, #dc2626, #991b1b)' },
];

export function ShortsRow() {
  const [playing, setPlaying] = useState<string | null>(null);
  const [progress, setProgress] = useState<Record<string, number>>({});
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (playing) {
      intervalRef.current = setInterval(() => {
        setProgress(prev => {
          const cur = prev[playing] || 0;
          if (cur >= 100) {
            // Auto-advance to next short
            const idx = MOCK_SHORTS.findIndex(s => s.id === playing);
            const next = MOCK_SHORTS[idx + 1];
            if (next) {
              setPlaying(next.id);
              return { ...prev, [playing]: 0 };
            }
            setPlaying(null);
            return { ...prev, [playing]: 0 };
          }
          return { ...prev, [playing]: cur + 1 };
        });
      }, 100);
    } else if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [playing]);

  return (
    <div className="social-box" style={{ overflow: 'hidden' }}>
      <div className="flex items-center justify-between px-3 py-1.5" style={{ background: 'var(--box-header-bg)', borderBottom: '1px solid var(--box-header-border)' }}>
        <span className="font-bold text-xs" style={{ color: 'var(--box-header-text)' }}>🎬 Shorts</span>
        <span className="text-2xs font-bold cursor-pointer hover:underline" style={{ color: 'var(--box-header-text)' }}>See All →</span>
      </div>
      <div className="flex gap-2 p-2 overflow-x-auto" style={{ scrollbarWidth: 'thin' }}>
        {MOCK_SHORTS.map(short => {
          const isPlaying = playing === short.id;
          const pct = progress[short.id] || 0;

          return (
            <div
              key={short.id}
              className="shrink-0 relative flex flex-col items-center justify-end cursor-pointer"
              style={{
                width: '110px',
                height: '180px',
                background: short.gradient,
                border: isPlaying ? '2px solid var(--accent)' : '1px solid var(--border-card)',
              }}
              onClick={() => {
                if (isPlaying) {
                  setPlaying(null);
                } else {
                  setProgress(prev => ({ ...prev, [short.id]: 0 }));
                  setPlaying(short.id);
                }
              }}
            >
              {/* Play overlay */}
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-3xl" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
                  {isPlaying ? '⏸️' : short.emoji}
                </span>
              </div>

              {/* Progress bar */}
              {(isPlaying || pct > 0) && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ background: 'rgba(0,0,0,0.3)' }}>
                  <div
                    style={{
                      width: `${pct}%`,
                      height: '100%',
                      background: 'white',
                      transition: 'width 0.1s linear',
                    }}
                  />
                </div>
              )}

              {/* Bottom info */}
              <div className="relative z-10 w-full p-1.5" style={{ background: 'linear-gradient(transparent, rgba(0,0,0,0.8))' }}>
                <div className="font-bold text-white truncate" style={{ fontSize: '10px' }}>{short.title}</div>
                <div className="flex justify-between items-center" style={{ fontSize: '9px', color: 'rgba(255,255,255,0.7)' }}>
                  <span>{short.author}</span>
                  <span>{short.views}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
